import { PropsWithChildren, createContext, useEffect, useRef, useState } from "react";
import { serverTimestamp, setDoc } from "firebase/firestore";
import { getDistanceInMeters } from "shared/session-utils";

import { auth } from "../firebase/auth";
import { getLastKnownLocationRef } from "../firebase/firestore";

const LOCATION_UPDATE_INTERVAL_MS = 30_000; // 30 seconds
const LOCATION_UPDATE_MIN_DISTANCE_M = 50; // 50 metres
const LOCATION_TIMEOUT_MS = 15_000; // 15 seconds

interface ILocationContext {
  location?: GeolocationPosition;
  isLoading?: boolean;
  error?: unknown;
  requestLocation?: () => void;
}

export const LocationContext = createContext<ILocationContext>({});

interface Props extends PropsWithChildren {
  options: PositionOptions;
}

export function LocationProvider(props: Props) {
  const [isLoading, setIsLoading] = useState(true);
  const [location, setLocation] = useState<GeolocationPosition>();
  const [error, setError] = useState<unknown>();
  const lastWrittenRef = useRef<{
    lat: number;
    lon: number;
    time: number;
  } | null>(null);
  const resolvedRef = useRef(false);
  const watchIdRef = useRef<number | null>(null);
  const fallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  async function updateLastKnownLocation(geolocation: GeolocationPosition) {
    const user = auth.currentUser;

    if (!user) {
      return;
    }

    const { latitude: lat, longitude: lon } = geolocation.coords;
    const now = Date.now();
    const last = lastWrittenRef.current;

    if (last) {
      const elapsed = now - last.time;
      const moved = getDistanceInMeters({ lat: last.lat, lon: last.lon }, { lat, lon });
      if (elapsed < LOCATION_UPDATE_INTERVAL_MS && moved < LOCATION_UPDATE_MIN_DISTANCE_M) {
        return;
      }
    }

    try {
      await setDoc(getLastKnownLocationRef(user.uid), {
        latitude: lat,
        longitude: lon,
        updated: serverTimestamp(),
      });
      lastWrittenRef.current = { lat, lon, time: now };
    } catch (updateError) {
      console.error("Unable to update last known location", updateError);
    }
  }

  function handleGeolocationSuccess(geolocation: GeolocationPosition) {
    resolvedRef.current = true;
    setIsLoading(false);
    setError(undefined);
    setLocation(geolocation);
    void updateLastKnownLocation(geolocation);
  }

  function handleGeolocationError(error: unknown) {
    resolvedRef.current = true;
    setIsLoading(false);
    setError(error);
  }

  function requestLocation() {
    // In AI mode use a fixed Cardiff city-centre location so check-in works
    // without requiring real GPS access in automated / headless environments.
    if (import.meta.env.VITE_AI_MODE === "true") {
      handleGeolocationSuccess({
        coords: {
          latitude: 51.4816,
          longitude: -3.1791,
          accuracy: 100,
          altitude: null,
          altitudeAccuracy: null,
          heading: null,
          speed: null,
        },
        timestamp: Date.now(),
      } as GeolocationPosition);
      return;
    }

    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
    if (fallbackTimeoutRef.current !== null) {
      clearTimeout(fallbackTimeoutRef.current);
    }

    resolvedRef.current = false;
    setIsLoading(true);
    setError(undefined);

    // Belt-and-braces: some browsers never call back at all if the
    // permission prompt is left unanswered, even though the spec's
    // PositionOptions.timeout should cover that case. Without this, isLoading
    // would stay true forever and the UI would show a permanent spinner.
    fallbackTimeoutRef.current = setTimeout(() => {
      if (!resolvedRef.current) {
        handleGeolocationError(new Error("Timed out waiting for location"));
      }
    }, LOCATION_TIMEOUT_MS);

    // PositionOptions.timeout defaults to Infinity when unset, so without an
    // explicit value a browser that never resolves the permission prompt
    // would leave isLoading stuck at true forever.
    const options: PositionOptions = {
      timeout: LOCATION_TIMEOUT_MS,
      ...props.options,
    };

    navigator.geolocation.getCurrentPosition(
      handleGeolocationSuccess,
      handleGeolocationError,
      options,
    );

    watchIdRef.current = navigator.geolocation.watchPosition(
      handleGeolocationSuccess,
      handleGeolocationError,
      options,
    );
  }

  useEffect(() => {
    requestLocation();

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (fallbackTimeoutRef.current !== null) {
        clearTimeout(fallbackTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally mount-only: options is an inline object literal that would re-register on every render

  return (
    <LocationContext.Provider value={{ location, isLoading, error, requestLocation }}>
      {props.children}
    </LocationContext.Provider>
  );
}
